import { Order } from "./order";
import { DayOfWeek } from "./enums/day-of-week.enum";
import { Product } from "../product/product";

export interface BarChartData {
  data: Array<number>;
  labels: Array<string>;
  dates: Array<Date>;
}

export interface ISummary {
  units: number;
  avg_price: number;
  subtotal: number;
  service_fees: number;
  tips: number;
  tip_percentage: number;
  total: number;
  redeemed: number;
  redeemed_percentage: number;
  revenue_percentage: number;
}

export class Totals {
  revenue: number;
  units_sold: number;
  unique_customers: number;
  new_customers: number;
  active_venues: number;

  constructor() {
    this.unique_customers = 0;
    this.new_customers = 0;
    this.units_sold = 0;
    this.revenue = 0;
    this.active_venues = 0;
  }
}

export abstract class Summary implements ISummary {
  orders: Order[];
  units: number;
  avg_price: number;
  subtotal: number;
  service_fees: number;
  tips: number;
  tip_percentage: number;
  total: number;
  redeemed: number;
  redeemed_percentage: number;
  revenue_percentage: number;
  constructor(orders: Order[], revenue: number) {
    this.orders = orders;
    this.reduce(revenue);
  }

  init = () => ({
    units: 0,
    subtotal: 0,
    avg_price: 0,
    total: 0,
    redeemed: 0,
    redeemed_percentage: 0,
    service_fees: 0,
    tips: 0,
    revenue_percentage: 0,
    tip_percentage: 0
  });

  reduce(revenue: number) {
    let r = this.orders.reduce((res, order) => {
      res.units += order.items[0].summary.units;
      res.subtotal += order.items[0].summary.price;
      res.total += order.summary.total;
      res.redeemed += order.items[0].summary.redeemedAt != null ? 1 : 0;
      res.service_fees += order.summary.serviceFee;
      res.tips += order.summary.tip;
      res.tip_percentage += order.summary.tip == 0 ? 0 : 1;
      return res;
    }, this.init());
    this.units = r.units;
    this.subtotal = r.subtotal;
    this.avg_price = r.units == 0 ? 0 : r.subtotal / r.units;
    this.total = r.total;
    this.redeemed = r.redeemed;
    this.redeemed_percentage =
      r.redeemed == 0 ? 0 : r.redeemed / this.orders.length;
    this.service_fees = r.service_fees;
    this.tips = r.tips;
    this.revenue_percentage = revenue == 0 ? 0 : r.total / revenue;
    this.tip_percentage = r.tip_percentage / this.orders.length;
  }

  round(num: number) {
    return Math.round(num * 100 + Number.EPSILON) / 100;
  }

  hasTip(product: Product): boolean {
    return false;
  }
  getServiceFee(product: Product): number {
    return 0;
  }
}

export class ByProductType extends Summary {
  product: string;
  constructor(orders: Order[], type: string, revenue: number) {
    super(orders, revenue);
    this.product = type;
  }
}

export class ByCity extends Summary {
  city: string;
  venues: number;
  constructor(orders: Order[], revenue: number, city: string) {
    super(orders, revenue);
    this.city = city;
    this.venues = this.getVenueCount();
  }

  getVenueCount(): number {
    return this.orders.reduce((acc, order) => {
      if (!acc.includes(order.venue.name)) acc.push(order.venue.name);
      return acc;
    }, []).length;
  }
}

export class ByVenue extends Summary {
  venue: string;
  city: string;
  constructor(orders: Order[], revenue: number, venue: string, city: string) {
    super(orders, revenue);
    this.city = city;
    this.venue = venue;
  }
}

export class ByDayOfWeek extends Summary {
  day: string;
  number_of_days: number;

  constructor(orders: Order[], day: number, revenue: number) {
    super(orders, revenue);
    this.day = DayOfWeek[day];
    this.number_of_days = this._getDays();
  }

  _getDays() {
    var days: string[] = this.orders.map(order => {
      var temp: Date = order.items[0].event.date.toDate();
      var s =
        temp.getMonth() + 1 + "/" + temp.getDate() + "/" + temp.getFullYear();
      return s;
    });
    return new Set(days).size;
  }
}

export class ByActiveDays extends Summary {
  day_of_week: string;
  date: Date;
  constructor(orders: Order[], date: Date, revenue: number) {
    super(orders, revenue);
    this.date = date;
    this.day_of_week = DayOfWeek[date.getDay()];
  }
}
